import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { useMultiSelect } from '../../../../hooks/useMultiSelect'
import { useApprovalSearchParams } from '../../hooks/useApprovalSearchParams'
import { buildApprovalUserOptions } from '../../utils/approvalFilterOptions'
import {
  approveProductivityAdjustment,
  fetchProductivityAdjustmentApprovalUsers,
  fetchProductivityAdjustmentApprovalProjects,
  fetchProductivityAdjustmentApprovalTasks,
  fetchProductivityAdjustmentApprovalQueue,
  rejectProductivityAdjustment,
  updateProductivityAdjustmentRequestedTime,
  bulkApproveProductivityAdjustments,
  bulkRejectProductivityAdjustments,
} from '../../../productivity/api/productivityApi'

const DEFAULT_PAGE_SIZE = 10

function toIsoDate(value) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getCurrentMonthRange() {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  return {
    from: toIsoDate(start),
    to: toIsoDate(today),
  }
}

export function useProductivityAdjustmentApprovalScreen() {
  const { searchParams, updateParam, updateParams, setPageParam } = useApprovalSearchParams()
  const initialRange = useMemo(() => getCurrentMonthRange(), [])

  const page = Number(searchParams.get('page') || 1)
  const searchText = searchParams.get('search') || ''
  const searchField = searchParams.get('search_field') || 'ALL'
  const statusFilter = searchParams.get('status') || 'PENDING'
  const userFilter = searchParams.get('user_id') || 'all'
  const projectFilter = searchParams.get('project_id') || 'All'
  const taskFilter = searchParams.get('task_id') || 'All'
  const fromDate = searchParams.get('fromDate') ?? initialRange.from
  const toDate = searchParams.get('toDate') ?? initialRange.to

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [userOptions, setUserOptions] = useState([])
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [totals, setTotals] = useState({ pending: 0, approved: 0, rejected: 0 })
  const [actionError, setActionError] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [activeActionId, setActiveActionId] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)
  
  const { isMultiSelectMode, selectedIds, toggleMultiSelectMode, toggleSelection, selectAll, clearSelection } = useMultiSelect()

  const queryParams = useMemo(() => {
    const params = {
      page,
      pageSize,
      includeTotal: true,
      status: statusFilter,
    }
    if (searchText.trim()) params.search = searchText.trim()
    params.search_field = searchField
    if (fromDate) params.date_from = fromDate
    if (toDate) params.date_to = toDate
    if (userFilter !== 'all') params.user_id = Number(userFilter)
    if (projectFilter !== 'All' && projectFilter !== 'all') params.project_id = Number(projectFilter)
    if (taskFilter !== 'All' && taskFilter !== 'all') params.task_id = Number(taskFilter)
    return params
  }, [page, pageSize, statusFilter, userFilter, projectFilter, taskFilter, fromDate, toDate, searchText, searchField])

  useEffect(() => {
    let active = true
    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await fetchProductivityAdjustmentApprovalQueue(queryParams)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRows(items)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
        setTotals(response?.approvalSummary || { pending: 0, approved: 0, rejected: 0 })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setError(extractApiErrorMessage(requestError, 'Failed to load productivity-hour approval queue.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams, refreshTick])

  useEffect(() => {
    let active = true
    async function loadUserOptions() {
      try {
        const options = await fetchProductivityAdjustmentApprovalUsers({
          status: statusFilter,
          date_from: fromDate || undefined,
          date_to: toDate || undefined,
        })
        if (!active) return
        setUserOptions(Array.isArray(options) ? options : [])
      } catch {
        if (!active) return
        // Keep prior options; URL state must not be cleared by an option error.
      }
    }
    loadUserOptions()
    return () => {
      active = false
    }
  }, [statusFilter, fromDate, toDate])

  const mappedRows = useMemo(() => {
    return rows.map((row) => ({
      id: Number(row.id),
      userName: row.userName || '-',
      reasonLabel: row.reasonLabel || '-',
      manualActiveSeconds: Number(row.manualActiveSeconds || 0),
      manualActiveLabel: row.manualActiveLabel || '0m',
      taskId: row.taskId ? Number(row.taskId) : null,
      taskName: row.taskName || null,
      taskCode: row.taskCode || null,
      projectId: row.projectId ? Number(row.projectId) : null,
      projectName: row.projectName || null,
      projectCode: row.projectCode || null,
      departmentName: row.departmentName || null,
      reportingManagerName: row.reportingManagerName || null,
      remark: row.remark || '-',
      approvalStatus: String(row.approvalStatus || 'PENDING').toUpperCase(),
      decisionRemark: row.decisionRemark || '-',
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      sessionLoginAt: row.sessionLoginAt,
      sessionLogoutAt: row.sessionLogoutAt,
      canApprove: Boolean(row.canApprove),
      canEditRequestedTime: Boolean(row.canEditRequestedTime),
    }))
  }, [rows])

  const optionContext = useMemo(() => ({
    status: statusFilter,
    ...(userFilter !== 'all' ? { user_id: Number(userFilter) } : {}),
    ...(fromDate ? { date_from: fromDate } : {}),
    ...(toDate ? { date_to: toDate } : {}),
  }), [statusFilter, userFilter, fromDate, toDate])

  const fetchProjectFilterOptions = useCallback((search, optionPage, optionPageSize) => (
    fetchProductivityAdjustmentApprovalProjects(search, optionPage, optionPageSize, {
      ...optionContext,
      ...(taskFilter !== 'All' && taskFilter !== 'all' ? { task_id: Number(taskFilter) } : {}),
    })
  ), [optionContext, taskFilter])

  const fetchTaskFilterOptions = useCallback((search, optionPage, optionPageSize, selectedProjectId) => (
    fetchProductivityAdjustmentApprovalTasks(search, optionPage, optionPageSize, {
      ...optionContext,
      ...(selectedProjectId && selectedProjectId !== 'All' && selectedProjectId !== 'all'
        ? { project_id: Number(selectedProjectId) }
        : {}),
    })
  ), [optionContext])

  const selectedProjectLabel = useMemo(() => {
    if (projectFilter === 'All' || projectFilter === 'all') return ''
    const row = mappedRows.find((item) => String(item.projectId) === String(projectFilter))
    if (!row) return `Project #${projectFilter}`
    return row.projectCode ? `${row.projectName || `Project #${projectFilter}`} (${row.projectCode})` : row.projectName
  }, [mappedRows, projectFilter])

  const selectedTaskLabel = useMemo(() => {
    if (taskFilter === 'All' || taskFilter === 'all') return ''
    const row = mappedRows.find((item) => String(item.taskId) === String(taskFilter))
    if (!row) return `Task #${taskFilter}`
    return row.taskCode ? `${row.taskName || `Task #${taskFilter}`} (${row.taskCode})` : row.taskName
  }, [mappedRows, taskFilter])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!mappedRows.length) return 0
    return (page - 1) * pageSize + 1
  }, [mappedRows.length, page, pageSize])

  const rangeEnd = useMemo(() => {
    if (!mappedRows.length) return 0
    return rangeStart + mappedRows.length - 1
  }, [mappedRows.length, rangeStart])



  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPageParam(nextPage)
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  async function runAction(adjustmentId, actionType, decisionRemark = '') {
    setActionError('')
    setActionMessage('')
    setActiveActionId(adjustmentId)
    try {
      if (actionType === 'approve') {
        await approveProductivityAdjustment(adjustmentId, {})
        setActionMessage('Productivity adjustment approved successfully.')
      } else if (actionType === 'reject') {
        await rejectProductivityAdjustment(adjustmentId, { decisionRemark })
        setActionMessage('Productivity adjustment rejected successfully.')
      } else if (actionType === 'edit') {
        const payload = typeof decisionRemark === 'object' && decisionRemark !== null
          ? decisionRemark
          : { manualActiveSeconds: Number(decisionRemark), taskId: null }
        await updateProductivityAdjustmentRequestedTime(adjustmentId, {
          manualActiveSeconds: Number(payload.manualActiveSeconds || 0),
          projectId: payload.projectId ? Number(payload.projectId) : undefined,
          taskId: payload.taskId ? Number(payload.taskId) : undefined,
        })
        setActionMessage('Requested productivity entry updated successfully.')
      }
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Action failed.'))
    } finally {
      setActiveActionId(null)
    }
  }


  async function runBulkApprovalAction(actionType) {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    setActionError('')
    setActionMessage('')
    try {
      if (actionType === 'approve') {
        await bulkApproveProductivityAdjustments({ ids })
        setActionMessage(`Successfully approved ${ids.length} adjustment(s).`)
      } else {
        await bulkRejectProductivityAdjustments({ ids })
        setActionMessage(`Successfully rejected ${ids.length} adjustment(s).`)
      }
      clearSelection()
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Bulk action failed.'))
    }
  }

  return {
    state: {
      isLoading,
      error,
      actionError,
      actionMessage,
      activeActionId,
      rows: mappedRows,
      page,
      totalPages,
      pageTokens: buildPaginationTokens(page, totalPages),
      totalCount: meta.total,
      rangeStart,
      rangeEnd,
      searchText,
      searchField,
      statusFilter,
      userFilter,
      userOptions: buildApprovalUserOptions(userOptions, userFilter),
      projectFilter,
      taskFilter,
      selectedProjectLabel,
      selectedTaskLabel,
      fromDate,
      toDate,
      totals,
      isMultiSelectMode,
      selectedIds,
    },
    actions: {
      setSearchText: (value) => updateParam('search', value),
      setSearchField: (value) => updateParam('search_field', value),
      setStatusFilter: (value) => updateParam('status', value),
      setUserFilter: (value) => updateParam('user_id', value),
      setProjectFilter: (value) => updateParams({ project_id: value, task_id: 'All' }),
      setTaskFilter: (value) => updateParam('task_id', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      goToPreviousPage,
      goToNextPage,
      goToPage,
      fetchProjectFilterOptions,
      fetchTaskFilterOptions,
      approveAdjustment: (adjustmentId) => runAction(adjustmentId, 'approve'),
      rejectAdjustment: (adjustmentId, decisionRemark = '') => runAction(adjustmentId, 'reject', decisionRemark),
      editRequestedTime: (adjustmentId, payload) => runAction(adjustmentId, 'edit', payload),
      toggleMultiSelectMode,
      toggleSelection,
      selectAll: () => {
        const selectable = mappedRows.filter(r => r.approvalStatus === 'PENDING' && r.canApprove).map(r => r.id)
        selectAll(selectable)
      },
      bulkApprove: () => runBulkApprovalAction('approve'),
      bulkReject: () => runBulkApprovalAction('reject'),
    },
  }
}
